/**
 * @file registerShared.ts
 * @brief Non-component pieces of web sign-up: the password rules, the
 *        post-signup option lists, the live availability hook and the
 *        error wording.
 *
 * @details
 * Kept apart from the `.tsx` files so those export components only (a mixed
 * module loses React Fast Refresh, see buttonVariants.ts).
 *
 * The lists mirror the phone (FRONTEND/src/shared/validation/password.ts,
 * FRONTEND/src/features/auth/onboardingApi.ts) and the server allowlists in
 * BACKEND/src/features/auth/routes.js: add an option on the server first or
 * the answer comes back a 400.
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ApiClientError } from "@/shared/api/client";

// ----------------------------------------------------------------------
// Password policy (BACKEND/src/shared/validation/password.js)
// ----------------------------------------------------------------------
export const PASSWORD_RULES: { test: (p: string) => boolean; message: string }[] = [
  { test: (p) => p.length >= 8, message: "At least 8 characters" },
  { test: (p) => /[A-Z]/.test(p), message: "One uppercase letter" },
  { test: (p) => /[a-z]/.test(p), message: "One lowercase letter" },
  { test: (p) => /[0-9]/.test(p), message: "One number" },
  { test: (p) => /[!@#$%^&*()_+\-=[\]{};:'",.<>/?\\|]/.test(p), message: "One symbol" },
  { test: (p) => !/\s/.test(p), message: "No spaces" },
];

export const passwordFormSchema = z
  .object({
    password: z.string().refine((p) => PASSWORD_RULES.every((r) => r.test(p)), {
      message: "Password doesn't meet the requirements",
    }),
    confirm: z.string().min(1, "Confirm your password"),
  })
  .refine((d) => d.password === d.confirm, {
    message: "Passwords don't match",
    path: ["confirm"],
  });

export type PasswordFormValues = z.infer<typeof passwordFormSchema>;

/** The phone's phone-number rule (RegisterPersonal.tsx). */
export const phoneNumberSchema = z
  .string()
  .min(10, "Phone number must be at least 10 digits")
  .regex(/^[+]?[(]?[0-9\s\-().]{10,}$/, "Enter a valid phone number");

// ----------------------------------------------------------------------
// Post-signup questions
// ----------------------------------------------------------------------
export const SIGNUP_SOURCES: readonly { code: string; label: string }[] = [
  { code: "friend", label: "Friend or colleague" },
  { code: "work", label: "Work or employer" },
  { code: "search", label: "Search engine" },
  { code: "app_store", label: "App Store" },
  { code: "social", label: "Social media" },
  { code: "video", label: "YouTube or TikTok" },
  { code: "linkedin", label: "LinkedIn" },
  { code: "ai_chat", label: "AI chat" },
  { code: "newsletter", label: "Newsletter or blog" },
  { code: "news", label: "News or press" },
  { code: "event", label: "Event or conference" },
  { code: "other", label: "Something else" },
];
export const SIGNUP_SOURCE_DETAIL_CODE = "other";
export const SIGNUP_SOURCE_DETAIL_MAX = 200;

export const PRIMARY_USES: readonly { code: string; label: string }[] = [
  { code: "meetings", label: "Meetings" },
  { code: "interpretation", label: "Interpretation" },
  { code: "notes", label: "Personal notes" },
  { code: "medical", label: "Medical assessments" },
  { code: "home", label: "A bit of everything" },
];

/** What the login page receives after a finished sign-up. */
export type RegisteredState = {
  registeredEmail: string;
  accountType: "personal" | "business";
};

// ----------------------------------------------------------------------
// Live "already taken" check
// ----------------------------------------------------------------------
/**
 * Asks `check` about `value` 450ms after typing stops. Only a definite
 * "taken" flags the field; an unknown answer (offline, rate-limited) stays
 * quiet, and the submit asks once more.
 */
export function useTakenCheck(
  value: string,
  enabled: boolean,
  check: (v: string) => Promise<{ available: boolean; message?: string } | null>,
): { takenValue: string | null; message: string | null } {
  const [result, setResult] = useState<{ takenValue: string | null; message: string | null }>({
    takenValue: null,
    message: null,
  });
  const seq = useRef(0);
  const checkRef = useRef(check);
  checkRef.current = check;

  useEffect(() => {
    const mine = ++seq.current;
    if (!enabled) return;
    const t = window.setTimeout(async () => {
      const r = await checkRef.current(value);
      if (mine !== seq.current) return;
      setResult(
        r && !r.available
          ? { takenValue: value, message: r.message ?? null }
          : { takenValue: null, message: null },
      );
    }, 450);
    return () => window.clearTimeout(t);
  }, [value, enabled]);

  return result.takenValue === value ? result : { takenValue: null, message: null };
}

// ----------------------------------------------------------------------
// Errors (BACKEND/src/features/auth/routes.js register + register-company)
// ----------------------------------------------------------------------
export function describeRegisterError(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case "EMAIL_EXISTS":
        return "This email is already registered. Sign in instead.";
      case "COMPANY_EXISTS":
        return "A company with this name already exists.";
      case "COMPANY_NAME_RESERVED":
        return "This company name is reserved.";
      case "RATE_LIMITED":
        return "Too many registration attempts. Please try again later.";
      default:
        return err.message || "We couldn't create your account. Please try again.";
    }
  }
  return "We couldn't create your account. Please try again.";
}
